import { T, useI18n } from '@/i18n';
import * as Popover from '@radix-ui/react-popover';
import { format } from 'date-fns';
import { enGB, lt, nb, pl } from 'date-fns/locale';
import { type InputHTMLAttributes, useEffect, useRef, useState } from 'react';
import { DayPicker } from 'react-day-picker';
import { validPeriod } from './DateJump';
import { Icon } from './icons';

/**
 * A date as the locale writes it (16.06.2027 in Norwegian), stored as ISO (2027-06-16) (#154 VF-07).
 * The text is shown and typed in the locale's form; ISO typed is taken too. The value handed on is the
 * ISO date once the text is a real date, '' when it is empty, and the text itself otherwise, so every
 * caller's own check of an unfinished or wrong date sees what it saw before. On leaving the field a
 * real date is written back in the locale's form.
 */
const FORMS: Record<string, { sep: string; order: 'dmy' | 'ymd'; hint: string }> = {
  nb: { sep: '.', order: 'dmy', hint: 'dd.mm.åååå' },
  pl: { sep: '.', order: 'dmy', hint: 'dd.mm.rrrr' },
  en: { sep: '/', order: 'dmy', hint: 'dd/mm/yyyy' },
  // Lithuanian writes dates as ISO
  lt: { sep: '-', order: 'ymd', hint: 'yyyy-mm-dd' },
};
const formOf = (locale: string) => FORMS[locale] ?? (FORMS.nb as (typeof FORMS)[string]);
/** The ISO date as the locale writes it; anything that is not one, unchanged. */
export function showDate(iso: string, locale: string): string {
  if (!validPeriod(iso, false)) return iso;
  const f = formOf(locale);
  const [y, m, d] = iso.split('-');
  return f.order === 'ymd' ? iso : `${d}${f.sep}${m}${f.sep}${y}`;
}
/** The ISO date a typed text means: the locale's form (day and month may be one digit) or ISO; null otherwise. */
export function readDate(text: string): string | null {
  const t = text.trim();
  if (validPeriod(t, false)) return t;
  const m = /^(\d{1,2})[./](\d{1,2})[./](\d{4})$/.exec(t);
  if (!m) return null;
  const iso = `${m[3]}-${(m[2] as string).padStart(2, '0')}-${(m[1] as string).padStart(2, '0')}`;
  return validPeriod(iso, false) ? iso : null;
}

/** Locale text entry and library calendar share the same controlled ISO value. */
export function DateInput({
  value: controlled,
  defaultValue,
  onValueChange,
  onCommit,
  ...props
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'defaultValue' | 'onChange' | 'type'> & {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  onCommit?: (value: string) => void;
}) {
  const [local, setLocal] = useState(defaultValue ?? '');
  const value = controlled ?? local;
  const locale = useI18n((s) => s.locale);
  // what the field shows: the value in the locale's form, or what is being typed
  const [text, setText] = useState(() => showDate(value, locale));
  // the field has the focus: what is in it is being typed
  const [editing, setEditing] = useState(false);
  const editingRef = useRef(false);
  // A value from outside (the calendar, a reset, another shift) or a new locale is shown in the
  // locale's form. Left alone: text being typed that the value only echoes, and text that is not a date
  // which the owner holds as it is. An idle real date is written again, so a locale change shows in it
  // (UI01-17).
  useEffect(() => {
    setText((t) => {
      const typed = readDate(t);
      if ((typed ?? t) !== value) return showDate(value, locale);
      if (editingRef.current || typed === null) return t;
      return showDate(value, locale);
    });
  }, [value, locale]);
  // Text that is not a date, in a field nobody is typing in, is marked; so whatever puts a real date in
  // (typing, the calendar, a reset from outside) unmarks it (UI01-16). An owner's own aria-invalid wins.
  const wrong = !editing && text.trim() !== '' && readDate(text) === null;
  const change = (next: string) => {
    setLocal(next);
    onValueChange?.(next);
  };
  const type = (t: string) => {
    setText(t);
    change(t.trim() === '' ? '' : (readDate(t) ?? t));
  };
  const [open, setOpen] = useState(false);
  const selected = validPeriod(value, false) ? new Date(`${value}T12:00:00`) : undefined;
  return (
    <span className="date-field">
      <input
        {...props}
        aria-invalid={props['aria-invalid'] ?? (wrong || undefined)}
        type="text"
        autoComplete="off"
        value={text}
        placeholder={formOf(locale).hint}
        onChange={(e) => type(e.target.value)}
        onFocus={(e) => {
          props.onFocus?.(e);
          editingRef.current = true;
          setEditing(true);
        }}
        onBlur={(e) => {
          props.onBlur?.(e);
          editingRef.current = false;
          setEditing(false);
          const typed = e.currentTarget.value;
          const iso = readDate(typed);
          if (iso) setText(showDate(iso, locale));
          if (!typed.trim() || iso) onCommit?.(iso ?? '');
        }}
      />
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <button
            type="button"
            disabled={props.disabled || props.readOnly}
            aria-label={`${T('Velg dato')}: ${props['aria-label'] ?? T('Dato')}`}
          >
            <Icon name="calendar" />
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content className="pop datejump-pop" sideOffset={6} collisionPadding={12}>
            <DayPicker
              mode="single"
              selected={selected}
              defaultMonth={selected ?? new Date()}
              locale={{ nb, en: enGB, lt, pl }[locale]}
              weekStartsOn={1}
              captionLayout="dropdown"
              startMonth={new Date(1900, 0)}
              endMonth={new Date(2100, 11)}
              disabled={[
                ...(props.min ? [{ before: new Date(`${props.min}T12:00:00`) }] : []),
                ...(props.max ? [{ after: new Date(`${props.max}T12:00:00`) }] : []),
              ]}
              onSelect={(date) => {
                if (date) {
                  const iso = format(date, 'yyyy-MM-dd');
                  setText(showDate(iso, locale));
                  change(iso);
                  onCommit?.(iso);
                  setOpen(false);
                }
              }}
            />
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </span>
  );
}
